import * as amplitude from "@amplitude/analytics-browser";
import { Box, Flex } from "@chakra-ui/react";
import React from "react";
import { Loading } from "src/components/Feedback/Loading";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  AmplitudeTrack,
  AmplitudeTrackEvent,
  DocumentLoadFailed,
  DocumentLoadFailedEvent,
  Ready,
} from "./CustomEvents";
import { DocumentViewerDebugger } from "./DocumentViewerDebugger";
import { DocumentViewerElement } from "./DocumentViewerElement";
import {
  DocumentViewerHeader,
  type DocumentViewerHighlightToggleProps,
} from "./DocumentViewerHeader";
import { DocumentViewerToolbar } from "./DocumentViewerToolbar";
import { HighlightNote } from "./HighlightNote";
import { Document, Highlight } from "./types";

// Define the custom element
if (!customElements.get("document-viewer")) {
  customElements.define("document-viewer", DocumentViewerElement);
}

export type Props = {
  autofocus?: boolean;
  document: Document | Document[];
  highlights?: readonly Highlight[];
  highlightToggle?: DocumentViewerHighlightToggleProps;
  onClose?: () => void;
  // Pass in documentIndex and onChangeDocumentIndex to control document index externally.
  documentIndex?: number;
  onChangeDocumentIndex?: (index: number) => void;
};
export const DocumentViewer = React.forwardRef<
  DocumentViewerElement | undefined,
  Props
>(
  (
    {
      document,
      highlights,
      highlightToggle,
      onClose,
      documentIndex,
      onChangeDocumentIndex,
      autofocus = true,
    },
    ref
  ) => {
    const internalRef = React.useRef<DocumentViewerElement>();
    const toast = useAvelaToast();
    const [internalDocumentIndex, setInternalDocumentIndex] =
      React.useState<number>(0);

    // Use the externally-controlled documentIndex if provided.
    const currentDocumentIndex = React.useMemo(() => {
      return documentIndex !== undefined
        ? documentIndex
        : internalDocumentIndex;
    }, [documentIndex, internalDocumentIndex]);

    React.useEffect(() => {
      if (documentIndex === undefined) {
        return;
      }
      setInternalDocumentIndex(documentIndex);
    }, [documentIndex]);

    const currentDocument: Document | undefined = React.useMemo(() => {
      return Array.isArray(document)
        ? document[currentDocumentIndex]
        : document;
    }, [currentDocumentIndex, document]);

    React.useImperativeHandle(
      ref,
      () => {
        return internalRef.current;
      },
      []
    );

    const onDocumentLoadFailed = React.useCallback(
      (event: DocumentLoadFailedEvent) => {
        console.error(event.detail.error);
        toast.error({
          title: "Error opening document",
          description: event.detail.error,
        });
      },
      [toast]
    );

    const [ready, setReady] = React.useState(false);
    const onReady = React.useCallback(() => {
      setReady(true);
    }, []);
    React.useEffect(() => {
      const documentViewer = internalRef.current;
      if (!documentViewer) {
        return;
      }

      documentViewer.addEventListener(Ready, onReady);
      documentViewer.addEventListener(DocumentLoadFailed, onDocumentLoadFailed);
      documentViewer.addEventListener(AmplitudeTrack, onAmplitudeTrack);
      return () => {
        documentViewer.removeEventListener(Ready, onReady);
        documentViewer.removeEventListener(
          DocumentLoadFailed,
          onDocumentLoadFailed
        );
        documentViewer.removeEventListener(AmplitudeTrack, onAmplitudeTrack);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canvasHighlightsOn = highlightToggle
      ? highlightToggle.highlightsOn
      : true;

    const [isLoading, setIsLoading] = React.useState(true);
    // setIsLoading before paint so we never call setHighlights while the previous file is still shown.
    React.useLayoutEffect(() => {
      if (!ready || !currentDocument) {
        return;
      }
      setIsLoading(true);
    }, [currentDocument, ready]);

    React.useEffect(() => {
      const documentViewer = internalRef.current;
      if (!documentViewer || !ready || isLoading) {
        return;
      }

      if (canvasHighlightsOn && highlights && highlights.length > 0) {
        documentViewer.setHighlights(highlights);
      } else {
        documentViewer.clearHighlights();
      }
    }, [canvasHighlightsOn, highlights, ready, isLoading]);

    React.useEffect(() => {
      const documentViewer = internalRef.current;
      if (!documentViewer || !ready || !currentDocument) {
        return;
      }

      documentViewer
        .loadDocument(currentDocument)
        .then(() => setIsLoading(false));
    }, [currentDocument, ready]);

    React.useEffect(() => {
      const documentViewer = internalRef.current;
      if (!documentViewer || !ready) {
        return;
      }

      if (autofocus) {
        documentViewer.focus();
      }
    }, [autofocus, ready]);

    if (!currentDocument) {
      return null;
    }

    return (
      <Flex
        width="100%"
        height="100%"
        direction="column"
        position="relative"
        sx={{ containerType: "inline-size" }}
      >
        <DocumentViewerHeader
          currentDocumentIndex={currentDocumentIndex}
          onClose={onClose}
          document={document}
          setCurrentDocumentIndex={(index) => {
            onChangeDocumentIndex?.(index);
            setInternalDocumentIndex(index);
          }}
          highlightToggle={highlightToggle}
        />
        <Box width="100%" height="100%" position="relative">
          <document-viewer ref={internalRef} />
          <HighlightNote documentViewerRef={internalRef} />
          {isLoading ? <Loading width="100%" height="100%" /> : null}
        </Box>
        <DocumentViewerToolbar
          documentViewerRef={internalRef}
          currentDocument={currentDocument}
        />
        <DocumentViewerDebugger documentViewerRef={internalRef} />
      </Flex>
    );
  }
);

function onAmplitudeTrack(event: AmplitudeTrackEvent) {
  amplitude.track(event.detail.name, event.detail.value);
}
